import { useState, useEffect } from 'react';
import { Calendar, DollarSign, BarChart3, MessageSquare, CalendarIcon } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/lib/supabase';
import { addDays, format, subDays } from 'date-fns';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// DateRange type copied from react-day-picker
interface DateRange {
  from: Date;
  to?: Date;
}

// Custom type for LLM cost data
interface LlmCostData {
  total_cost: number;
  calls_made: number;
  avg_cost_per_call: number;
  costs_by_model: Record<string, { total_cost: number; calls: number; avg_cost: number }>;
}

// Custom type for daily cost data
interface DailyCostData {
  day: string;
  model_name: string;
  total_calls: number;
  total_cost: number;
  avg_cost_per_call: number;
  total_tokens: number;
}

// Custom type for prompt cost data
interface PromptCostData {
  day: string;
  prompt_name: string;
  model_name: string;
  total_calls: number;
  total_cost: number;
  avg_cost_per_call: number;
  total_tokens: number;
  min_duration_ms?: number;
  avg_duration_ms?: number;
  max_duration_ms?: number;
  error_count?: number;
  error_rate?: number;
}

// COLORS array for pie chart
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#8dd1e1'];

export default function LlmUsageDashboard() {
  // State for date range
  const [date, setDate] = useState<DateRange>({
    from: subDays(new Date(), 30),
    to: new Date(),
  });

  // State for cost data
  const [costData, setCostData] = useState<LlmCostData | null>(null);
  const [dailyCosts, setDailyCosts] = useState<DailyCostData[]>([]);
  const [promptCosts, setPromptCosts] = useState<PromptCostData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch data based on date range
  useEffect(() => {
    const fetchLlmCostData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Format dates for Postgres
        const fromDateStr = date.from ? format(date.from, 'yyyy-MM-dd') : '';
        const toDateStr = date.to ? format(addDays(date.to, 1), 'yyyy-MM-dd') : ''; // Include the end date

        // Get LLM cost summary
        const { data: costData, error: costError } = await supabase.rpc(
          'get_llm_costs',
          {
            start_date: fromDateStr,
            end_date: toDateStr
          }
        );

        if (costError) {
          console.error('Error from get_llm_costs:', costError);
          throw new Error(costError.message);
        }

        // Check if we have data and handle it
        if (costData && costData.length > 0) {
          const data = costData[0];
          
          // Ensure costs_by_model is parsed correctly
          let costsObj = {};
          
          if (typeof data.costs_by_model === 'string') {
            // If returned as a string, parse it
            try {
              costsObj = JSON.parse(data.costs_by_model);
            } catch (e) {
              console.warn('Failed to parse costs_by_model as JSON:', e);
              costsObj = {};
            }
          } else {
            // It's already an object
            costsObj = data.costs_by_model || {};
          }
          
          setCostData({
            total_cost: Number(data.total_cost) || 0,
            calls_made: Number(data.calls_made) || 0,
            avg_cost_per_call: Number(data.avg_cost_per_call) || 0,
            costs_by_model: costsObj
          });
        } else {
          // Set default values if no data
          setCostData({
            total_cost: 0,
            calls_made: 0,
            avg_cost_per_call: 0,
            costs_by_model: {}
          });
        }

        // Get daily cost breakdown
        const { data: dailyData, error: dailyError } = await supabase
          .from('daily_llm_costs')
          .select('*')
          .gte('day', fromDateStr)
          .lt('day', toDateStr)
          .order('day', { ascending: true });

        if (dailyError) {
          console.error('Error from daily_llm_costs:', dailyError);
          throw new Error(dailyError.message);
        }
        
        // Ensure we convert the API response to our DailyCostData type
        if (dailyData) {
          setDailyCosts(dailyData.map(item => ({
            day: item.day as string,
            model_name: item.model_name as string,
            total_calls: Number(item.total_calls) || 0,
            total_cost: Number(item.total_cost) || 0,
            avg_cost_per_call: Number(item.avg_cost_per_call) || 0,
            total_tokens: Number(item.total_tokens) || 0
          })));
        } else {
          setDailyCosts([]);
        }
        
        // Get prompt cost breakdown
        const { data: promptData, error: promptError } = await supabase
          .from('prompt_llm_costs')
          .select('*')
          .gte('day', fromDateStr)
          .lt('day', toDateStr)
          .order('day', { ascending: true });

        if (promptError) {
          console.error('Error from prompt_llm_costs:', promptError);
          throw new Error(promptError.message);
        }
        
        // Ensure we convert the API response to our PromptCostData type
        if (promptData) {
          setPromptCosts(promptData.map(item => ({
            day: item.day as string,
            prompt_name: item.prompt_name as string,
            model_name: item.model_name as string,
            total_calls: Number(item.total_calls) || 0,
            total_cost: Number(item.total_cost) || 0,
            avg_cost_per_call: Number(item.avg_cost_per_call) || 0,
            total_tokens: Number(item.total_tokens) || 0,
            min_duration_ms: Number(item.min_duration_ms) || 0,
            avg_duration_ms: Number(item.avg_duration_ms) || 0,
            max_duration_ms: Number(item.max_duration_ms) || 0,
            error_count: Number(item.error_count) || 0,
            error_rate: Number(item.error_rate) || 0
          })));
        } else {
          setPromptCosts([]);
        }

      } catch (err) {
        console.error('Error fetching LLM data:', err);
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
        // Set default values on error
        setCostData({
          total_cost: 0,
          calls_made: 0,
          avg_cost_per_call: 0,
          costs_by_model: {}
        });
        setDailyCosts([]);
      } finally {
        setLoading(false);
      }
    };

    if (date.from && date.to) {
      fetchLlmCostData();
    }
  }, [date]);

  // Prepare data for charts
  const modelData = costData?.costs_by_model 
    ? Object.entries(costData.costs_by_model).map(([model, data]) => ({
        name: model,
        value: typeof data.total_cost === 'number' ? parseFloat(data.total_cost.toFixed(6)) : 0,
        calls: typeof data.calls === 'number' ? data.calls : 0,
        avgCost: typeof data.avg_cost === 'number' ? parseFloat(data.avg_cost.toFixed(6)) : 0
      }))
    : [];

  // Format daily data for line chart
  const dailyChartData = dailyCosts.reduce((acc, item) => {
    const day = format(new Date(item.day), 'MMM dd');
    const existingDay = acc.find(d => d.day === day);
    
    if (existingDay) {
      existingDay.totalCost += parseFloat((item.total_cost || 0).toFixed(6));
      existingDay.totalCalls += item.total_calls || 0;
      existingDay.totalTokens += item.total_tokens || 0;
    } else {
      acc.push({
        day,
        totalCost: parseFloat((item.total_cost || 0).toFixed(6)),
        totalCalls: item.total_calls || 0,
        totalTokens: item.total_tokens || 0
      });
    }
    
    return acc;
  }, [] as Array<{day: string; totalCost: number; totalCalls: number; totalTokens: number}>);
  
  // Format prompt data for charts
  const promptChartData = promptCosts.reduce((acc, item) => {
    const promptName = item.prompt_name || 'unknown';
    const existingPrompt = acc.find(p => p.name === promptName);
    
    if (existingPrompt) {
      existingPrompt.value += parseFloat((item.total_cost || 0).toFixed(6));
      existingPrompt.calls += item.total_calls || 0;
      existingPrompt.tokens += item.total_tokens || 0;
      existingPrompt.avgDuration = (existingPrompt.avgDuration * existingPrompt.callCount + (item.avg_duration_ms || 0)) / (existingPrompt.callCount + 1);
      existingPrompt.callCount += 1;
      existingPrompt.errorRate = (existingPrompt.errorRate * existingPrompt.callCount + (item.error_rate || 0)) / (existingPrompt.callCount + 1);
    } else {
      acc.push({
        name: promptName,
        value: parseFloat((item.total_cost || 0).toFixed(6)),
        calls: item.total_calls || 0,
        tokens: item.total_tokens || 0,
        avgDuration: item.avg_duration_ms || 0,
        errorRate: item.error_rate || 0,
        callCount: 1
      });
    }
    
    return acc;
  }, [] as Array<{
    name: string;
    value: number;
    calls: number;
    tokens: number;
    avgDuration: number;
    errorRate: number;
    callCount: number;
  }>).sort((a, b) => b.value - a.value);

  // Format prompt-model combination data for the new analysis tab
  const promptModelData = promptCosts.reduce((acc, item) => {
    const key = `${item.prompt_name || 'unknown'}_${item.model_name || 'unknown'}`;
    const existing = acc.find(p => p.key === key);

    if (existing) {
      existing.totalCost += parseFloat((item.total_cost || 0).toFixed(6));
      existing.totalCalls += item.total_calls || 0;
      existing.totalTokens += item.total_tokens || 0;
      existing.avgDuration = (existing.avgDuration * existing.callCount + (item.avg_duration_ms || 0)) / (existing.callCount + 1);
      existing.callCount += 1;
    } else {
      acc.push({
        key,
        promptName: item.prompt_name || 'unknown',
        modelName: item.model_name || 'unknown',
        totalCost: parseFloat((item.total_cost || 0).toFixed(6)),
        totalCalls: item.total_calls || 0,
        totalTokens: item.total_tokens || 0,
        avgDuration: item.avg_duration_ms || 0,
        avgCostPerCall: parseFloat(((item.total_cost || 0) / Math.max(item.total_calls || 1, 1)).toFixed(6)),
        callCount: 1
      });
    }

    return acc;
  }, [] as Array<{
    key: string;
    promptName: string;
    modelName: string;
    totalCost: number;
    totalCalls: number;
    totalTokens: number;
    avgDuration: number;
    avgCostPerCall: number;
    callCount: number;
  }>).sort((a, b) => b.totalCost - a.totalCost);

  // Group data by prompt for the stacked bar chart
  const promptModelChartData = promptCosts.reduce((acc, item) => {
    const promptName = item.prompt_name || 'unknown';
    const modelName = item.model_name || 'unknown';
    const existingPrompt = acc.find(p => p.name === promptName);

    if (existingPrompt) {
      if (existingPrompt[modelName]) {
        existingPrompt[modelName] += parseFloat((item.total_cost || 0).toFixed(6));
      } else {
        existingPrompt[modelName] = parseFloat((item.total_cost || 0).toFixed(6));
      }
      existingPrompt.totalCost += parseFloat((item.total_cost || 0).toFixed(6));
    } else {
      const newEntry: any = {
        name: promptName,
        totalCost: parseFloat((item.total_cost || 0).toFixed(6)),
        [modelName]: parseFloat((item.total_cost || 0).toFixed(6))
      };
      acc.push(newEntry);
    }

    return acc;
  }, [] as Array<any>).sort((a, b) => b.totalCost - a.totalCost);

  // Get unique models for the chart legend
  const uniqueModels = [...new Set(promptCosts.map(item => item.model_name || 'unknown'))];

  // Generate colors for each model
  const modelColors = uniqueModels.reduce((acc, model, index) => {
    acc[model] = COLORS[index % COLORS.length];
    return acc;
  }, {} as Record<string, string>);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <h2 className="text-2xl font-bold">LLM Usage Dashboard</h2>
        <DatePickerWithRange date={date} setDate={setDate} />
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatsCard 
          title="Total Cost" 
          value={loading ? undefined : `$${(costData?.total_cost || 0).toFixed(4)}`}
          description="Total spending on LLM API calls"
          icon={<DollarSign className="h-4 w-4 text-muted-foreground" />}
        />
        <StatsCard 
          title="API Calls" 
          value={loading ? undefined : `${(costData?.calls_made || 0).toLocaleString()}`}
          description="Number of LLM API calls made"
          icon={<MessageSquare className="h-4 w-4 text-muted-foreground" />}
        />
        <StatsCard 
          title="Avg. Cost/Call" 
          value={loading ? undefined : `$${(costData?.avg_cost_per_call || 0).toFixed(6)}`}
          description="Average cost per API call"
          icon={<BarChart3 className="h-4 w-4 text-muted-foreground" />}
        />
        <StatsCard 
          title="Date Range" 
          value={loading ? undefined : `${date.from ? format(date.from, 'MMM d') : ''} - ${date.to ? format(date.to, 'MMM d') : ''}`}
          description="Current analysis period"
          icon={<Calendar className="h-4 w-4 text-muted-foreground" />}
        />
      </div>

      {/* Charts */}
      <Tabs defaultValue="cost">
        <TabsList className="grid w-full md:w-auto md:inline-grid grid-cols-5">
          <TabsTrigger value="cost">Cost Analysis</TabsTrigger>
          <TabsTrigger value="usage">Usage Trends</TabsTrigger>
          <TabsTrigger value="models">Model Breakdown</TabsTrigger>
          <TabsTrigger value="prompts">Prompt Breakdown</TabsTrigger>
          <TabsTrigger value="prompt-models">Prompt-Model Analysis</TabsTrigger>
        </TabsList>

        {/* Cost Analysis Tab */}
        <TabsContent value="cost" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Daily Cost Trend</CardTitle>
              <CardDescription>LLM API costs over time</CardDescription>
            </CardHeader>
            <CardContent className="h-[400px]">
              {loading ? (
                <Skeleton className="w-full h-full" />
              ) : dailyChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={dailyChartData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="day" />
                    <YAxis yAxisId="left" orientation="left" stroke="#8884d8" />
                    <YAxis yAxisId="right" orientation="right" stroke="#82ca9d" />
                    <Tooltip formatter={(value) => [`$${value}`, 'Cost']} />
                    <Legend />
                    <Line yAxisId="left" type="monotone" dataKey="totalCost" name="Daily Cost ($)" stroke="#8884d8" activeDot={{ r: 8 }} />
                    <Line yAxisId="right" type="monotone" dataKey="totalCalls" name="API Calls" stroke="#82ca9d" />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-muted-foreground">No data available for the selected period</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Usage Trends Tab */}
        <TabsContent value="usage" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Token Usage</CardTitle>
              <CardDescription>Daily token consumption</CardDescription>
            </CardHeader>
            <CardContent className="h-[400px]">
              {loading ? (
                <Skeleton className="w-full h-full" />
              ) : dailyChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={dailyChartData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="day" />
                    <YAxis />
                    <Tooltip formatter={(value) => [value.toLocaleString(), 'Tokens']} />
                    <Legend />
                    <Bar dataKey="totalTokens" name="Tokens Used" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-muted-foreground">No data available for the selected period</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Model Breakdown Tab */}
        <TabsContent value="models" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Cost by Model</CardTitle>
              <CardDescription>Breakdown of costs by LLM model</CardDescription>
            </CardHeader>
            <CardContent className="h-[400px]">
              {loading ? (
                <Skeleton className="w-full h-full" />
              ) : modelData.length > 0 ? (
                <div className="grid md:grid-cols-2 gap-4 h-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={modelData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      >
                        {modelData.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [`$${value}`, 'Cost']} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={modelData}
                      layout="vertical"
                      margin={{ top: 20, right: 30, left: 40, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis type="category" dataKey="name" width={100} />
                      <Tooltip formatter={(value) => [`${value}`, 'Calls']} />
                      <Legend />
                      <Bar dataKey="calls" name="Number of Calls" fill="#82ca9d" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-muted-foreground">No data available for the selected period</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Prompt Breakdown Tab */}
        <TabsContent value="prompts" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Cost by Prompt</CardTitle>
              <CardDescription>Breakdown of costs by prompt name</CardDescription>
            </CardHeader>
            <CardContent className="h-[600px]">
              {loading ? (
                <Skeleton className="w-full h-full" />
              ) : promptChartData.length > 0 ? (
                <div className="grid md:grid-cols-2 gap-4 h-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={promptChartData.slice(0, 8)} // Show top 8 prompts
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, percent }) => `${name.substring(0, 15)}${name.length > 15 ? '...' : ''}: ${(percent * 100).toFixed(0)}%`}
                      >
                        {promptChartData.slice(0, 8).map((_, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [`$${value}`, 'Cost']} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="overflow-auto h-full">
                    <table className="w-full text-sm">
                      <thead>
                        <tr>
                          <th className="text-left p-2">Prompt Name</th>
                          <th className="text-right p-2">Cost</th>
                          <th className="text-right p-2">Calls</th>
                          <th className="text-right p-2">Avg. MS</th>
                          <th className="text-right p-2">Error %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {promptChartData.map((prompt, index) => (
                          <tr key={index} className={index % 2 === 0 ? 'bg-muted/30' : ''}>
                            <td className="p-2 truncate max-w-[160px]" title={prompt.name}>{prompt.name}</td>
                            <td className="text-right p-2">${prompt.value.toFixed(4)}</td>
                            <td className="text-right p-2">{prompt.calls.toLocaleString()}</td>
                            <td className="text-right p-2">{Math.round(prompt.avgDuration).toLocaleString()}</td>
                            <td className="text-right p-2">{prompt.errorRate.toFixed(1)}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-muted-foreground">No prompt data available for the selected period</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Prompt-Model Analysis Tab */}
        <TabsContent value="prompt-models" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {/* Stacked Bar Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Cost by Prompt and Model</CardTitle>
                <CardDescription>Stacked view showing which models each prompt uses</CardDescription>
              </CardHeader>
              <CardContent className="h-[400px]">
                {loading ? (
                  <Skeleton className="w-full h-full" />
                ) : promptModelChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={promptModelChartData.slice(0, 10)} // Show top 10 prompts
                      margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="name"
                        angle={-45}
                        textAnchor="end"
                        height={80}
                        interval={0}
                        fontSize={10}
                      />
                      <YAxis />
                      <Tooltip
                        formatter={(value, name) => [`$${value}`, name]}
                        labelFormatter={(label) => `Prompt: ${label}`}
                      />
                      <Legend />
                      {uniqueModels.map((model) => (
                        <Bar
                          key={model}
                          dataKey={model}
                          stackId="cost"
                          name={model}
                          fill={modelColors[model]}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-muted-foreground">No prompt-model data available for the selected period</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Model Distribution by Prompt */}
            <Card>
              <CardHeader>
                <CardTitle>Top Prompt-Model Combinations</CardTitle>
                <CardDescription>Highest cost prompt-model pairs</CardDescription>
              </CardHeader>
              <CardContent className="h-[400px]">
                {loading ? (
                  <Skeleton className="w-full h-full" />
                ) : promptModelData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={promptModelData.slice(0, 10)}
                      layout="vertical"
                      margin={{ top: 20, right: 30, left: 120, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis
                        type="category"
                        dataKey="key"
                        tickFormatter={(value) => {
                          const [prompt, model] = value.split('_');
                          return `${prompt.substring(0, 15)}${prompt.length > 15 ? '...' : ''} (${model})`;
                        }}
                        width={110}
                        fontSize={10}
                      />
                      <Tooltip
                        formatter={(value) => [`$${value}`, 'Cost']}
                        labelFormatter={(label) => {
                          const [prompt, model] = label.split('_');
                          return `${prompt} using ${model}`;
                        }}
                      />
                      <Bar dataKey="totalCost" name="Total Cost" fill="#8884d8" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-muted-foreground">No prompt-model data available for the selected period</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Detailed Table */}
          <Card>
            <CardHeader>
              <CardTitle>Detailed Prompt-Model Analysis</CardTitle>
              <CardDescription>Complete breakdown of prompt-model cost relationships</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="w-full h-[300px]" />
              ) : promptModelData.length > 0 ? (
                <div className="overflow-auto max-h-[500px]">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-background">
                      <tr className="border-b">
                        <th className="text-left p-3 font-medium">Prompt Name</th>
                        <th className="text-left p-3 font-medium">Model Name</th>
                        <th className="text-right p-3 font-medium">Total Cost</th>
                        <th className="text-right p-3 font-medium">Total Calls</th>
                        <th className="text-right p-3 font-medium">Avg. Cost/Call</th>
                        <th className="text-right p-3 font-medium">Total Tokens</th>
                        <th className="text-right p-3 font-medium">Avg. Duration (ms)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {promptModelData.map((item, index) => (
                        <tr key={index} className={index % 2 === 0 ? 'bg-muted/30' : ''}>
                          <td className="p-3 truncate max-w-[200px]" title={item.promptName}>
                            {item.promptName}
                          </td>
                          <td className="p-3 font-mono text-xs">
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-md">
                              {item.modelName}
                            </span>
                          </td>
                          <td className="text-right p-3 font-medium">
                            ${item.totalCost.toFixed(4)}
                          </td>
                          <td className="text-right p-3">
                            {item.totalCalls.toLocaleString()}
                          </td>
                          <td className="text-right p-3">
                            ${item.avgCostPerCall.toFixed(6)}
                          </td>
                          <td className="text-right p-3">
                            {item.totalTokens.toLocaleString()}
                          </td>
                          <td className="text-right p-3">
                            {Math.round(item.avgDuration).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="flex items-center justify-center h-[200px]">
                  <p className="text-muted-foreground">No prompt-model data available for the selected period</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Summary Insights */}
          <Card>
            <CardHeader>
              <CardTitle>Cost Optimization Insights</CardTitle>
              <CardDescription>Key findings and recommendations</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="w-full h-[100px]" />
              ) : promptModelData.length > 0 ? (
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-medium text-sm text-muted-foreground mb-2">Most Expensive Combination</h4>
                    {promptModelData.length > 0 && (
                      <div>
                        <p className="font-medium">{promptModelData[0].promptName}</p>
                        <p className="text-sm text-muted-foreground">using {promptModelData[0].modelName}</p>
                        <p className="text-lg font-bold text-red-600">${promptModelData[0].totalCost.toFixed(4)}</p>
                      </div>
                    )}
                  </div>
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-medium text-sm text-muted-foreground mb-2">Total Combinations</h4>
                    <p className="text-2xl font-bold">{promptModelData.length}</p>
                    <p className="text-sm text-muted-foreground">Unique prompt-model pairs</p>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-medium text-sm text-muted-foreground mb-2">Models in Use</h4>
                    <p className="text-2xl font-bold">{uniqueModels.length}</p>
                    <p className="text-sm text-muted-foreground">Different models across all prompts</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-[100px]">
                  <p className="text-muted-foreground">No data available for insights</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Custom DatePickerWithRange component (inline to avoid additional dependencies)
interface DatePickerWithRangeProps {
  date: DateRange;
  setDate: React.Dispatch<React.SetStateAction<DateRange>>;
  className?: string;
}

function DatePickerWithRange({ date, setDate, className }: DatePickerWithRangeProps) {
  return (
    <div className={cn("grid gap-2", className)}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={"outline"}
            className={cn(
              "w-[300px] justify-start text-left font-normal",
              !date && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {date?.from ? (
              date.to ? (
                <>
                  {format(date.from, "LLL dd, y")} - {format(date.to, "LLL dd, y")}
                </>
              ) : (
                format(date.from, "LLL dd, y")
              )
            ) : (
              <span>Pick a date range</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <div className="grid grid-cols-2 gap-2 p-4">
            <div>
              <Button
                variant="outline"
                className="w-full mb-2"
                onClick={() => setDate({ from: subDays(new Date(), 7), to: new Date() })}
              >
                Last 7 days
              </Button>
              <Button
                variant="outline"
                className="w-full mb-2"
                onClick={() => setDate({ from: subDays(new Date(), 30), to: new Date() })}
              >
                Last 30 days
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setDate({ from: subDays(new Date(), 90), to: new Date() })}
              >
                Last 90 days
              </Button>
            </div>
            <div>
              <Button
                variant="outline"
                className="w-full mb-2"
                onClick={() => {
                  const now = new Date();
                  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
                  setDate({ from: startOfMonth, to: now });
                }}
              >
                This Month
              </Button>
              <Button
                variant="outline"
                className="w-full mb-2"
                onClick={() => {
                  const now = new Date();
                  const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                  const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
                  setDate({ from: startOfLastMonth, to: endOfLastMonth });
                }}
              >
                Last Month
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  const now = new Date();
                  const startOfYear = new Date(now.getFullYear(), 0, 1);
                  setDate({ from: startOfYear, to: now });
                }}
              >
                Year to Date
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

// Stats Card Component
interface StatsCardProps {
  title: string;
  value?: string;
  description: string;
  icon: React.ReactNode;
}

function StatsCard({ title, value, description, icon }: StatsCardProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        {value ? (
          <div className="text-2xl font-bold">{value}</div>
        ) : (
          <Skeleton className="h-8 w-[100px]" />
        )}
        <p className="text-xs text-muted-foreground">{description}</p>
      </CardContent>
    </Card>
  );
} 